import { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { toast } from "sonner";
import { Dices, Plus, Trash2, Swords, BookOpen, Volume2 } from "lucide-react";
import RollPrivacyControl from "./RollPrivacyControl";
import DamageMitigationEditor from "./DamageMitigationEditor";
import { CustomFeaturesSection } from "./CustomFeaturesSection";
import SoundPicker from "./SoundPicker";
import AbilityBreakdownPopover from "./AbilityBreakdownPopover";
import TraitBlurb, { cap } from "./TraitBlurb";
import SheetSection from "./SheetSection";
import Dnd5eAsiPanel from "./Dnd5eAsiPanel";

const ABILITIES = ["str", "dex", "con", "int", "wis", "cha"];
const SKILLS = ["acrobatics", "animal_handling", "arcana", "athletics", "deception", "history", "insight", "intimidation", "investigation", "medicine", "nature", "perception", "performance", "persuasion", "religion", "sleight_of_hand", "stealth", "survival"];

export default function DndCharacterSheet({ gameId, character, onCharacterUpdate, isGm, members, myMemberId }) {
  const [stats, setStats] = useState(null);
  const [rollPrivacy, setRollPrivacy] = useState({ is_private: false, whisper_target_member_id: null });
  const [weaponsCatalog, setWeaponsCatalog] = useState([]);
  const [spellsCatalog, setSpellsCatalog] = useState([]);
  const [featsCatalog, setFeatsCatalog] = useState([]);
  const [racesCatalog, setRacesCatalog] = useState([]);
  const [backgroundsCatalog, setBackgroundsCatalog] = useState([]);
  const [weaponPick, setWeaponPick] = useState("");
  const [spellPick, setSpellPick] = useState("");
  const [featPick, setFeatPick] = useState("");
  const [sfxOpenIdx, setSfxOpenIdx] = useState(new Set());
  const [openAbilityBreakdown, setOpenAbilityBreakdown] = useState(null);
  const dnd = character.dnd5e || {};

  const loadStats = useCallback(async () => {
    const { data } = await api.get(`/characters/${character.id}/dnd5e-stats`);
    setStats(data);
  }, [character.id]);

  useEffect(() => { loadStats(); }, [loadStats, character]);
  useEffect(() => {
    api.get("/dnd5e/weapons").then((r) => setWeaponsCatalog(r.data));
    api.get("/dnd5e/spells").then((r) => setSpellsCatalog(r.data));
    api.get("/dnd5e/feats").then((r) => setFeatsCatalog(r.data));
    api.get("/dnd5e/races").then((r) => setRacesCatalog(r.data));
    api.get("/dnd5e/backgrounds").then((r) => setBackgroundsCatalog(r.data));
  }, []);

  const norm = (s) => (s || "").toLowerCase().trim();
  const raceObj = racesCatalog.find((r) => norm(r.name) === norm(dnd.race)) || null;
  const backgroundObj = backgroundsCatalog.find((b) => norm(b.name) === norm(dnd.background)) || null;

  const patch = async (updates) => {
    const { data } = await api.patch(`/characters/${character.id}`, updates);
    onCharacterUpdate(data);
  };
  const patchDnd = (updates) => patch({ dnd5e: { ...dnd, ...updates } });

  const roll = async (label, modifier) => {
    try {
      await api.post(`/characters/${character.id}/roll`, {
        game_id: gameId, label, modifier,
        is_private: rollPrivacy.is_private, whisper_target_member_id: rollPrivacy.whisper_target_member_id,
      });
      if (rollPrivacy.whisper_target_member_id) {
        const targetName = (members || []).find((m) => m.id === rollPrivacy.whisper_target_member_id)?.display_name || "player";
        toast.success(`Rolled secretly — only you and ${targetName} can see this`);
      } else if (rollPrivacy.is_private) {
        toast.success(isGm ? "Rolled privately — only you can see this" : "Rolled privately — only you and the GM can see this");
      }
    } catch {
      toast.error("Roll failed");
    }
  };

  const rollDamage = async (label, dice) => {
    if (!dice?.trim()) return toast.error("Enter a damage formula first, e.g. 1d8+3");
    try {
      await api.post(`/characters/${character.id}/damage-roll`, {
        game_id: gameId, label, dice, bonus: 0,
        is_private: rollPrivacy.is_private, whisper_target_member_id: rollPrivacy.whisper_target_member_id,
      });
    } catch {
      toast.error("Could not parse that damage formula");
    }
  };

  if (!stats) return <div className="p-6 text-sm text-[#f1e0c5]/40">Loading sheet…</div>;

  const RollButton = ({ label, modifier, testid }) => (
    <button data-testid={testid} onClick={() => roll(label, modifier)} className="w-7 h-7 rounded-full bg-[#D90429]/20 hover:bg-[#D90429] text-[#D90429] hover:text-white flex items-center justify-center shrink-0 transition-colors duration-200">
      <Dices className="w-3 h-3" />
    </button>
  );

  const attacks = dnd.attacks || [];
  const setAttack = (idx, field, value) => {
    const next = attacks.map((a, i) => (i === idx ? { ...a, [field]: value } : a));
    patchDnd({ attacks: next });
  };
  const addAttack = () => patchDnd({ attacks: [...attacks, { name: "", atk_bonus: "", damage: "" }] });
  const removeAttack = (idx) => patchDnd({ attacks: attacks.filter((_, i) => i !== idx) });
  const toggleSfxOpen = (idx) => {
    setSfxOpenIdx((prev) => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  };
  const addAttackFromCatalog = () => {
    const weapon = weaponsCatalog.find((w) => w.slug === weaponPick);
    if (!weapon) return;
    const rangeFt = weapon.range ? parseInt(weapon.range.split("/")[0], 10) : null;
    patchDnd({ attacks: [...attacks, { name: weapon.name, atk_bonus: "", damage: weapon.damage_dice, damage_type: weapon.damage_type, range_ft: rangeFt }] });
    setWeaponPick("");
  };

  const knownSpells = dnd.known_spells || [];
  const removeSpell = (idx) => patchDnd({ known_spells: knownSpells.filter((_, i) => i !== idx) });
  const addSpellFromCatalog = () => {
    const spell = spellsCatalog.find((s) => s.slug === spellPick);
    if (!spell) return;
    patchDnd({ known_spells: [...knownSpells, { name: spell.name, level: spell.level, school: spell.school, description: spell.description }] });
    setSpellPick("");
  };
  const addCustomSpell = () => patchDnd({ known_spells: [...knownSpells, { name: "", level: 0, school: "", description: "" }] });
  const setKnownSpell = (idx, field, value) => {
    const next = knownSpells.map((s, i) => (i === idx ? { ...s, [field]: value } : s));
    patchDnd({ known_spells: next });
  };

  const feats = dnd.feats || [];
  const removeFeat = (idx) => patchDnd({ feats: feats.filter((_, i) => i !== idx) });
  const addFeatFromCatalog = () => {
    const feat = featsCatalog.find((f) => f.slug === featPick);
    if (!feat) return;
    patchDnd({ feats: [...feats, { name: feat.name, prerequisite: feat.prerequisite, description: feat.description }] });
    setFeatPick("");
  };

  const deathSaves = dnd.death_saves || { successes: 0, failures: 0 };
  const setDeathSave = (key, count) => patchDnd({ death_saves: { ...deathSaves, [key]: count } });
  const rollDeathSave = async () => {
    try {
      const { data } = await api.post(`/characters/${character.id}/death-save`, { game_id: gameId });
      onCharacterUpdate(data.character);
      if (data.outcome === "critical_success_heal") toast.success("Natural 20! Gasping back to 1 HP!");
      else if (data.died) toast.error("3rd failure — the character has died.");
      else if (data.stabilized) toast.success("3rd success — stabilized!");
    } catch {
      toast.error("Death save roll failed");
    }
  };

  const currency = character.currency || { cp: 0, sp: 0, ep: 0, gp: 0, pp: 0 };

  return (
    <div className="p-4 h-full overflow-y-auto scrollbar-thin space-y-4" data-testid="dnd-character-sheet">
      <div className="flex items-center justify-between gap-2">
        <Input data-testid="dnd-character-name" value={character.name} onChange={(e) => patch({ name: e.target.value })} className="bg-black/30 border-[#4a3424]/50 font-display text-lg flex-1" />
        <Input data-testid="dnd-character-level" type="number" min={1} max={20} value={character.level || 1} onChange={(e) => patch({ level: parseInt(e.target.value, 10) || 1 })} className="bg-black/30 border-[#4a3424]/50 h-9 w-16 text-center" title="Level" />
      </div>
      <RollPrivacyControl isGm={isGm} members={members} myMemberId={myMemberId} value={rollPrivacy} onChange={setRollPrivacy} />

      <div className="grid grid-cols-2 gap-2">
        <div>
          <Input data-testid="dnd-race-field" placeholder="Race" value={dnd.race || ""} onChange={(e) => patchDnd({ race: e.target.value })} className="bg-black/20 border-[#4a3424]/50 h-8 text-xs" />
          {raceObj && (
            <TraitBlurb
              testId="dnd-race-trait-blurb"
              description={raceObj.description}
              facts={[`Speed ${raceObj.speed}ft`, `Size: ${cap(raceObj.size)}`]}
            />
          )}
        </div>
        <Input data-testid="dnd-class-field" placeholder="Class" value={dnd.class_name || ""} onChange={(e) => patchDnd({ class_name: e.target.value })} className="bg-black/20 border-[#4a3424]/50 h-8 text-xs" />
        <div>
          <Input data-testid="dnd-background-field" placeholder="Background" value={dnd.background || ""} onChange={(e) => patchDnd({ background: e.target.value })} className="bg-black/20 border-[#4a3424]/50 h-8 text-xs" />
          {backgroundObj && (
            <TraitBlurb testId="dnd-background-trait-blurb" description={backgroundObj.description} facts={(backgroundObj.ability_options || []).map((a) => `${a.toUpperCase()} +1`)} />
          )}
        </div>
        <Input data-testid="dnd-alignment-field" placeholder="Alignment" value={dnd.alignment || ""} onChange={(e) => patchDnd({ alignment: e.target.value })} className="bg-black/20 border-[#4a3424]/50 h-8 text-xs" />
      </div>

      <div>
        <p className="text-[10px] uppercase text-[#f1e0c5]/40 mb-2">Ability Scores</p>
        <div className="grid grid-cols-3 gap-2">
          {ABILITIES.map((a) => (
            <div key={a} className="relative bg-gradient-to-b from-[#2a1c12] to-[#1f150c] border-2 border-[#4a3424] border-b-[#1a1008] border-r-[#1a1008] rounded-xl shadow-[inset_0_2px_8px_rgba(0,0,0,0.4)] p-2 text-center">
              <label className="text-[10px] uppercase text-[#D4AF37]">{a}</label>
              <AbilityBreakdownPopover
                ability={a}
                history={character.boost_history}
                finalValue={character.abilities?.[a] ?? 10}
                open={openAbilityBreakdown === a}
                onToggle={() => setOpenAbilityBreakdown((cur) => (cur === a ? null : a))}
                testId={`dnd-ability-breakdown-panel-${a}`}
              />
              <Input
                data-testid={`dnd-ability-${a}`}
                type="number"
                value={character.abilities?.[a] ?? 10}
                onChange={(e) => patch({ abilities: { ...character.abilities, [a]: parseInt(e.target.value, 10) || 10 } })}
                className="bg-black/30 border-[#4a3424]/50 h-8 text-center"
              />
              <div className="flex items-center justify-center gap-1.5 mt-1">
                <span className="text-xs text-[#f1e0c5]/50">{stats.ability_mods[a] >= 0 ? "+" : ""}{stats.ability_mods[a]}</span>
                <RollButton label={a.toUpperCase()} modifier={stats.ability_mods[a]} testid={`dnd-roll-ability-${a}`} />
              </div>
            </div>
          ))}
        </div>
      </div>

      <Dnd5eAsiPanel character={character} patch={patch} />

      <div className="flex items-center justify-between bg-gradient-to-b from-[#2a1c12] to-[#1f150c] border-2 border-[#4a3424] border-b-[#1a1008] border-r-[#1a1008] rounded-xl shadow-[inset_0_2px_8px_rgba(0,0,0,0.4)] px-3 py-2">
        <label className="flex items-center gap-2 text-xs cursor-pointer">
          <input data-testid="dnd-inspiration-checkbox" type="checkbox" checked={!!dnd.inspiration} onChange={(e) => patchDnd({ inspiration: e.target.checked })} className="accent-[#D4AF37]" />
          Inspiration
        </label>
        <span className="text-xs text-[#f1e0c5]/50">Proficiency Bonus: <span className="text-[#D4AF37] font-semibold">+{stats.proficiency_bonus}</span></span>
      </div>

      <div className="space-y-1.5">
        <p className="text-[10px] uppercase text-[#f1e0c5]/40">Saving Throws</p>
        {ABILITIES.map((a) => (
          <div key={a} className="flex items-center justify-between gap-2 bg-black/20 border border-[#4a3424]/50 rounded-lg px-3 py-1.5">
            <label className="flex items-center gap-2 text-sm capitalize cursor-pointer flex-1">
              <input data-testid={`dnd-save-proficient-${a}`} type="checkbox" checked={!!dnd.save_proficiencies?.[a]} onChange={(e) => patchDnd({ save_proficiencies: { ...dnd.save_proficiencies, [a]: e.target.checked } })} className="accent-[#4d7c0f]" />
              {a} save
            </label>
            <span className="text-xs text-[#f1e0c5]/50 w-8 text-right">{stats.saves[a].modifier >= 0 ? "+" : ""}{stats.saves[a].modifier}</span>
            <RollButton label={`${a.toUpperCase()} Save`} modifier={stats.saves[a].modifier} testid={`dnd-roll-save-${a}`} />
          </div>
        ))}
      </div>

      <div className="space-y-1.5">
        <p className="text-[10px] uppercase text-[#f1e0c5]/40">Skills <span className="text-[#f1e0c5]/30">(Passive Perception {stats.passive_perception})</span></p>
        {SKILLS.map((s) => (
          <div key={s} className="flex items-center justify-between gap-2 bg-black/20 border border-[#4a3424]/50 rounded-lg px-3 py-1.5">
            <label className="flex items-center gap-2 text-sm capitalize cursor-pointer flex-1 truncate">
              <input data-testid={`dnd-skill-proficient-${s}`} type="checkbox" checked={character.skill_proficiencies?.[s] === "proficient"} onChange={(e) => patch({ skill_proficiencies: { ...character.skill_proficiencies, [s]: e.target.checked ? "proficient" : "none" } })} className="accent-[#4d7c0f]" />
              {s.replace(/_/g, " ")}
            </label>
            <span className="text-xs text-[#f1e0c5]/50 w-8 text-right">{stats.skills[s].modifier >= 0 ? "+" : ""}{stats.skills[s].modifier}</span>
            <RollButton label={s.replace(/_/g, " ")} modifier={stats.skills[s].modifier} testid={`dnd-roll-skill-${s}`} />
          </div>
        ))}
      </div>

      <div className="grid grid-cols-3 gap-2">
        <div className="bg-gradient-to-b from-[#2a1c12] to-[#1f150c] border-2 border-[#4a3424] border-b-[#1a1008] border-r-[#1a1008] rounded-xl shadow-[inset_0_2px_8px_rgba(0,0,0,0.4)] p-2 text-center">
          <label className="text-[10px] uppercase text-[#D4AF37]">AC</label>
          <Input data-testid="dnd-ac-input" type="number" value={dnd.ac ?? 10} onChange={(e) => patchDnd({ ac: parseInt(e.target.value, 10) || 10 })} className="bg-black/30 border-[#4a3424]/50 h-8 text-center" />
        </div>
        <div className="bg-gradient-to-b from-[#2a1c12] to-[#1f150c] border-2 border-[#4a3424] border-b-[#1a1008] border-r-[#1a1008] rounded-xl shadow-[inset_0_2px_8px_rgba(0,0,0,0.4)] p-2 text-center">
          <label className="text-[10px] uppercase text-[#D4AF37]">Initiative</label>
          <div className="flex items-center justify-center gap-1.5 mt-1.5">
            <span className="text-sm">{stats.initiative >= 0 ? "+" : ""}{stats.initiative}</span>
            <RollButton label="Initiative" modifier={stats.initiative} testid="dnd-roll-initiative" />
          </div>
        </div>
        <div className="bg-gradient-to-b from-[#2a1c12] to-[#1f150c] border-2 border-[#4a3424] border-b-[#1a1008] border-r-[#1a1008] rounded-xl shadow-[inset_0_2px_8px_rgba(0,0,0,0.4)] p-2 text-center">
          <label className="text-[10px] uppercase text-[#D4AF37]">Speed</label>
          <Input data-testid="dnd-speed-input" type="number" value={dnd.speed ?? 30} onChange={(e) => patchDnd({ speed: parseInt(e.target.value, 10) || 30 })} className="bg-black/30 border-[#4a3424]/50 h-8 text-center" />
        </div>
      </div>

      <div className="bg-gradient-to-b from-[#2a1c12] to-[#1f150c] border-2 border-[#4a3424] border-b-[#1a1008] border-r-[#1a1008] rounded-xl shadow-[inset_0_2px_8px_rgba(0,0,0,0.4)] p-3 space-y-2">
        <div className="flex items-center gap-2 justify-center">
          <span className="text-[10px] uppercase text-[#f1e0c5]/40">HP</span>
          <Input data-testid="dnd-hp-current-input" type="number" value={character.hp_current ?? dnd.hp_max ?? 10} onChange={(e) => patch({ hp_current: parseInt(e.target.value, 10) || 0 })} className="w-14 h-8 text-center bg-black/30 border-[#4a3424]/50" />
          <span className="text-[#f1e0c5]/40">/</span>
          <Input data-testid="dnd-hp-max-input" type="number" value={dnd.hp_max ?? 10} onChange={(e) => patchDnd({ hp_max: parseInt(e.target.value, 10) || 10 })} className="w-14 h-8 text-center bg-black/30 border-[#4a3424]/50" />
          <span className="text-[10px] uppercase text-[#f1e0c5]/40 ml-2">Temp</span>
          <Input data-testid="dnd-hp-temp-input" type="number" value={character.hp_temp ?? 0} onChange={(e) => patch({ hp_temp: parseInt(e.target.value, 10) || 0 })} className="w-12 h-8 text-center bg-black/30 border-[#4a3424]/50" />
        </div>
        <div className="flex items-center justify-center gap-2">
          <span className="text-[10px] uppercase text-[#f1e0c5]/40">Hit Dice</span>
          <Input data-testid="dnd-hit-dice-input" placeholder="3d8" value={dnd.hit_dice || ""} onChange={(e) => patchDnd({ hit_dice: e.target.value })} className="w-20 h-7 text-center bg-black/30 border-[#4a3424]/50 text-xs" />
        </div>
        <div className="flex items-center justify-center gap-4">
          <div className="flex items-center gap-1">
            <span className="text-[10px] text-[#4d7c0f] uppercase">Success</span>
            {[1, 2, 3].map((n) => (
              <button key={n} data-testid={`dnd-death-success-${n}`} onClick={() => setDeathSave("successes", deathSaves.successes >= n ? n - 1 : n)} className={`w-4 h-4 rounded-full border ${deathSaves.successes >= n ? "bg-[#4d7c0f] border-[#4d7c0f]" : "border-white/20"}`} />
            ))}
          </div>
          <div className="flex items-center gap-1">
            <span className="text-[10px] text-[#D90429] uppercase">Fail</span>
            {[1, 2, 3].map((n) => (
              <button key={n} data-testid={`dnd-death-failure-${n}`} onClick={() => setDeathSave("failures", deathSaves.failures >= n ? n - 1 : n)} className={`w-4 h-4 rounded-full border ${deathSaves.failures >= n ? "bg-[#D90429] border-[#D90429]" : "border-white/20"}`} />
            ))}
          </div>
        </div>
        {(character.hp_current ?? 1) <= 0 && (
          <button data-testid="dnd-roll-death-save-button" onClick={rollDeathSave} className="w-full rounded-full gap-2 h-8 flex items-center justify-center bg-[#D90429] hover:bg-[#b8031f] text-white text-xs">
            <Dices className="w-3.5 h-3.5" /> Roll Death Save
          </button>
        )}
      </div>

      <div>
        <div className="flex items-center justify-between mb-1.5">
          <p className="text-[10px] uppercase text-[#f1e0c5]/40">Attacks &amp; Spellcasting</p>
          <button data-testid="dnd-add-attack-button" onClick={addAttack} className="text-[#D4AF37] hover:text-white"><Plus className="w-3.5 h-3.5" /></button>
        </div>
        <div className="flex items-center gap-1.5 mb-1.5">
          <Select value={weaponPick} onValueChange={setWeaponPick}>
            <SelectTrigger data-testid="dnd-weapon-catalog-select" className="h-7 bg-black/30 border-[#4a3424]/50 text-[10px] flex-1"><SelectValue placeholder="Load a weapon from the compendium…" /></SelectTrigger>
            <SelectContent>{weaponsCatalog.map((w) => <SelectItem key={w.slug} value={w.slug}>{w.name} ({w.damage_dice} {w.damage_type})</SelectItem>)}</SelectContent>
          </Select>
          <Button data-testid="dnd-add-weapon-from-catalog" size="sm" variant="secondary" className="rounded-full h-7 text-[10px] gap-1 shrink-0" onClick={addAttackFromCatalog} disabled={!weaponPick}>
            <Swords className="w-3 h-3" /> Add
          </Button>
        </div>
        <div className="space-y-1.5">
          {attacks.map((atk, idx) => (
            <div key={idx} className="bg-black/20 border border-[#4a3424]/50 rounded-lg p-1.5 space-y-1.5">
              <div className="flex items-center gap-1.5 flex-wrap">
                <Input data-testid={`dnd-attack-name-${idx}`} placeholder="Name" value={atk.name} onChange={(e) => setAttack(idx, "name", e.target.value)} className="bg-black/30 border-[#4a3424]/50 h-7 text-xs flex-1 min-w-20" />
                <Input data-testid={`dnd-attack-bonus-${idx}`} placeholder="+5" value={atk.atk_bonus} onChange={(e) => setAttack(idx, "atk_bonus", e.target.value)} className="bg-black/30 border-[#4a3424]/50 h-7 text-xs w-14" />
                <button data-testid={`dnd-roll-attack-${idx}`} onClick={() => roll(atk.name || "Attack", parseInt(atk.atk_bonus, 10) || 0)} className="w-6 h-6 rounded-full bg-[#D90429]/20 hover:bg-[#D90429] text-[#D90429] hover:text-white flex items-center justify-center shrink-0"><Dices className="w-3 h-3" /></button>
                <Input data-testid={`dnd-attack-damage-${idx}`} placeholder="1d8+3" value={atk.damage} onChange={(e) => setAttack(idx, "damage", e.target.value)} className="bg-black/30 border-[#4a3424]/50 h-7 text-xs w-20" />
                <button data-testid={`dnd-roll-damage-${idx}`} onClick={() => rollDamage(atk.name || "Attack", atk.damage)} className="text-[9px] uppercase text-[#D4AF37]/70 hover:text-[#D4AF37] shrink-0">dmg</button>
                <Input data-testid={`dnd-attack-type-${idx}`} placeholder="type" value={atk.damage_type || ""} onChange={(e) => setAttack(idx, "damage_type", e.target.value)} className="bg-black/30 border-[#4a3424]/50 h-7 text-xs w-16" />
                <Input data-testid={`dnd-attack-range-${idx}`} placeholder="range ft" type="number" value={atk.range_ft || ""} onChange={(e) => setAttack(idx, "range_ft", e.target.value)} className="bg-black/30 border-[#4a3424]/50 h-7 text-xs w-16" title="Leave blank for melee (5ft)" />
                <button data-testid={`dnd-toggle-attack-sfx-${idx}`} onClick={() => toggleSfxOpen(idx)} className="text-[#f1e0c5]/30 hover:text-[#D4AF37] shrink-0" title="Hit/Miss sound overrides"><Volume2 className="w-3.5 h-3.5" /></button>
                <button data-testid={`dnd-remove-attack-${idx}`} onClick={() => removeAttack(idx)} className="text-[#f1e0c5]/30 hover:text-[#D90429] shrink-0"><Trash2 className="w-3 h-3" /></button>
              </div>
              {sfxOpenIdx.has(idx) && (
                <div className="space-y-1.5 pl-1 border-l border-[#4a3424]/50 ml-1">
                  <SoundPicker gameId={gameId} value={atk.hit_sfx_id} onChange={(id) => setAttack(idx, "hit_sfx_id", id)} label="Hit Sound" testId={`dnd-attack-hit-sound-${idx}`} />
                  <SoundPicker gameId={gameId} value={atk.miss_sfx_id} onChange={(id) => setAttack(idx, "miss_sfx_id", id)} label="Miss Sound" testId={`dnd-attack-miss-sound-${idx}`} />
                </div>
              )}
            </div>
          ))}
          {attacks.length === 0 && <p className="text-xs text-[#f1e0c5]/30 italic">No attacks yet — click + to add one.</p>}
        </div>
      </div>

      <div>
        <p className="text-[10px] uppercase text-[#f1e0c5]/40 mb-1.5">Spellbook</p>
        <div className="flex items-center gap-1.5 mb-1.5">
          <Select value={spellPick} onValueChange={setSpellPick}>
            <SelectTrigger data-testid="dnd-spell-catalog-select" className="h-7 bg-black/30 border-[#4a3424]/50 text-[10px] flex-1"><SelectValue placeholder="Add a spell from the compendium…" /></SelectTrigger>
            <SelectContent>{spellsCatalog.map((s) => <SelectItem key={s.slug} value={s.slug}>{s.level === 0 ? "Cantrip" : `Lv ${s.level}`} — {s.name}</SelectItem>)}</SelectContent>
          </Select>
          <Button data-testid="dnd-add-spell-from-catalog" size="sm" variant="secondary" className="rounded-full h-7 text-[10px] gap-1 shrink-0" onClick={addSpellFromCatalog} disabled={!spellPick}>
            <BookOpen className="w-3 h-3" /> Add
          </Button>
        </div>
        <div className="space-y-1.5">
          {knownSpells.map((sp, idx) => (
            <div key={idx} data-testid={`dnd-known-spell-${idx}`} className="flex items-start gap-1.5 bg-black/20 border border-[#4a3424]/50 rounded-lg p-1.5">
              <div className="flex-1 space-y-1">
                <div className="flex items-center gap-1.5">
                  <Input data-testid={`dnd-spell-name-${idx}`} placeholder="Spell name" value={sp.name} onChange={(e) => setKnownSpell(idx, "name", e.target.value)} className="bg-black/30 border-[#4a3424]/50 h-7 text-xs flex-1" />
                  <Input data-testid={`dnd-spell-level-${idx}`} type="number" min={0} max={9} value={sp.level ?? 0} onChange={(e) => setKnownSpell(idx, "level", parseInt(e.target.value, 10) || 0)} className="bg-black/30 border-[#4a3424]/50 h-7 text-xs w-14" title="Spell level (0 = cantrip)" />
                </div>
                {sp.description && <p className="text-[10px] text-[#f1e0c5]/40">{sp.description}</p>}
              </div>
              <button data-testid={`dnd-remove-spell-${idx}`} onClick={() => removeSpell(idx)} className="text-[#f1e0c5]/30 hover:text-[#D90429] shrink-0"><Trash2 className="w-3 h-3" /></button>
            </div>
          ))}
          {knownSpells.length === 0 && <p className="text-xs text-[#f1e0c5]/30 italic">No spells known yet.</p>}
        </div>
        <Button data-testid="dnd-add-custom-spell-button" variant="secondary" size="sm" className="w-full rounded-full mt-1.5 text-xs" onClick={addCustomSpell}>
          + Add Custom Spell
        </Button>
      </div>

      <div>
        <p className="text-[10px] uppercase text-[#f1e0c5]/40 mb-1.5">Feats</p>
        <div className="flex items-center gap-1.5 mb-1.5">
          <Select value={featPick} onValueChange={setFeatPick}>
            <SelectTrigger data-testid="dnd-feat-catalog-select" className="h-7 bg-black/30 border-[#4a3424]/50 text-[10px] flex-1"><SelectValue placeholder="Add a feat from the compendium…" /></SelectTrigger>
            <SelectContent>{featsCatalog.map((f) => <SelectItem key={f.slug} value={f.slug}>{f.name}</SelectItem>)}</SelectContent>
          </Select>
          <Button data-testid="dnd-add-feat-from-catalog" size="sm" variant="secondary" className="rounded-full h-7 text-[10px] gap-1 shrink-0" onClick={addFeatFromCatalog} disabled={!featPick}>
            <BookOpen className="w-3 h-3" /> Add
          </Button>
        </div>
        <div className="space-y-2">
          {feats.map((f, idx) => (
            <div key={idx} data-testid={`dnd-feat-${idx}`} className="flex items-start gap-2 bg-black/20 border border-[#4a3424]/50 rounded-lg px-3 py-2">
              <div className="flex-1">
                <p className="text-sm">{f.name}</p>
                {f.prerequisite && <p className="text-[10px] text-[#D4AF37]/70">Prerequisite: {f.prerequisite}</p>}
                {f.description && <p className="text-[10px] text-[#f1e0c5]/40">{f.description}</p>}
              </div>
              <button data-testid={`dnd-remove-feat-${idx}`} onClick={() => removeFeat(idx)} className="text-[#f1e0c5]/30 hover:text-[#D90429] shrink-0"><Trash2 className="w-3 h-3" /></button>
            </div>
          ))}
          {feats.length === 0 && <p className="text-xs text-[#f1e0c5]/30 italic">No feats yet — pick one above, or log anything not in our list under Custom Features below.</p>}
        </div>
      </div>

      <DamageMitigationEditor character={character} patchChar={patch} />

      <div className="grid grid-cols-2 gap-2">
        <Textarea data-testid="dnd-personality-traits" placeholder="Personality Traits" value={dnd.personality_traits || ""} onChange={(e) => patchDnd({ personality_traits: e.target.value })} className="bg-black/20 border-[#4a3424]/50 text-xs h-16" />
        <Textarea data-testid="dnd-ideals" placeholder="Ideals" value={dnd.ideals || ""} onChange={(e) => patchDnd({ ideals: e.target.value })} className="bg-black/20 border-[#4a3424]/50 text-xs h-16" />
        <Textarea data-testid="dnd-bonds" placeholder="Bonds" value={dnd.bonds || ""} onChange={(e) => patchDnd({ bonds: e.target.value })} className="bg-black/20 border-[#4a3424]/50 text-xs h-16" />
        <Textarea data-testid="dnd-flaws" placeholder="Flaws" value={dnd.flaws || ""} onChange={(e) => patchDnd({ flaws: e.target.value })} className="bg-black/20 border-[#4a3424]/50 text-xs h-16" />
      </div>

      <Textarea data-testid="dnd-other-profs-languages" placeholder="Other Proficiencies & Languages" value={dnd.other_profs_languages || ""} onChange={(e) => patchDnd({ other_profs_languages: e.target.value })} className="bg-black/20 border-[#4a3424]/50 text-xs h-16" />

      <div>
        <p className="text-[10px] uppercase text-[#f1e0c5]/40 mb-1.5">Equipment &amp; Currency</p>
        <div className="grid grid-cols-5 gap-1.5 mb-2">
          {["cp", "sp", "ep", "gp", "pp"].map((c) => (
            <div key={c}>
              <label className="text-[9px] uppercase text-[#D4AF37]">{c}</label>
              <Input data-testid={`dnd-currency-${c}`} type="number" value={currency[c] ?? 0} onChange={(e) => patch({ currency: { ...currency, [c]: parseInt(e.target.value, 10) || 0 } })} className="bg-black/30 border-[#4a3424]/50 h-7 text-xs text-center" />
            </div>
          ))}
        </div>
        <Textarea data-testid="dnd-equipment-text" placeholder="Equipment" value={dnd.equipment_text || ""} onChange={(e) => patchDnd({ equipment_text: e.target.value })} className="bg-black/20 border-[#4a3424]/50 text-xs h-20" />
      </div>

      <Textarea data-testid="dnd-features-text" placeholder="Features & Traits" value={dnd.features_text || ""} onChange={(e) => patchDnd({ features_text: e.target.value })} className="bg-black/20 border-[#4a3424]/50 text-xs h-20" />

      <SheetSection title="Custom Features / Feats" testId="dnd-sheet-section-custom-features">
        <CustomFeaturesSection character={character} patchChar={patch} />
      </SheetSection>
    </div>
  );
}
